/**
 * MeetingActionModal — unified Record / Schedule meeting flow for Contact page.
 *
 * Two tabs:
 *   1. Record: log a past meeting (creates an Activity + meeting_session with
 *      status="completed" so analysis + transcript can be attached later).
 *   2. Schedule: full scheduling flow — playbook, templates, signatures,
 *      side-by-side editable email + calendar invite, AI rewrite, send via
 *      Gmail/Google Calendar. A meeting_session is created in prep state so
 *      the desktop Copilot can pick it up with the playbook pre-selected.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import {
    Calendar as CalendarIcon, Mic, Mail, Loader2, Sparkles, BookOpen,
    FileText, PenLine, Send,
} from 'lucide-react';
import { useSendEmail, useGmailStatus } from '../hooks/useInbox';
import { useOutlookStatus } from '../hooks/useOutlook';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function todayYMD() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function applyMerge(str, vars) {
    if (!str) return '';
    return Object.entries(vars).reduce(
        (acc, [k, v]) => acc.split(`{{${k}}}`).join(v || ''),
        str
    );
}

export default function MeetingActionModal({
    open,
    onOpenChange,
    contact,
    account,
    deal,
    defaultTab = 'schedule',
    onDone,
    getAuthHeader,
}) {
    const [tab, setTab] = useState(defaultTab);
    useEffect(() => { if (open) setTab(defaultTab); }, [open, defaultTab]);

    const contactName = useMemo(() => {
        if (!contact) return '';
        return [contact.first_name, contact.last_name].filter(Boolean).join(' ');
    }, [contact]);

    // ───────── shared shape ─────────
    const [title, setTitle] = useState('');
    const [date, setDate] = useState(todayYMD());
    const [startTime, setStartTime] = useState('09:00');
    const [endTime, setEndTime] = useState('09:30');
    const [notes, setNotes] = useState('');
    const [playbookId, setPlaybookId] = useState('none');
    const [playbooks, setPlaybooks] = useState([]);

    useEffect(() => {
        if (!open) return;
        axios.get(`${API}/playbooks`, { headers: getAuthHeader() })
            .then(res => setPlaybooks(Array.isArray(res.data) ? res.data : (res.data?.playbooks || [])))
            .catch(() => setPlaybooks([]));
    }, [open, getAuthHeader]);

    // ───────── Schedule-only state ─────────
    const [location, setLocation] = useState('google_meet');
    const [templates, setTemplates] = useState([]);
    const [signatures, setSignatures] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('none');
    const [selectedSignatureId, setSelectedSignatureId] = useState('default');

    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [calendarDescription, setCalendarDescription] = useState('');

    const [aiRewriting, setAiRewriting] = useState(false);
    const [saving, setSaving] = useState(false);
    const { data: gmailStatus } = useGmailStatus();
    const { data: outlookStatus } = useOutlookStatus();
    const sendEmailMutation = useSendEmail();
    const gmailConnected = gmailStatus?.connected || false;
    const outlookConnected = outlookStatus?.connected || false;

    // Load templates + signatures when modal opens
    useEffect(() => {
        if (!open) return;
        Promise.all([
            axios.get(`${API}/email-templates`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
            axios.get(`${API}/signatures`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        ]).then(([tRes, sRes]) => {
            setTemplates(Array.isArray(tRes.data) ? tRes.data : (tRes.data?.templates || []));
            const sigs = Array.isArray(sRes.data) ? sRes.data : (sRes.data?.signatures || []);
            setSignatures(sigs);
            const def = sigs.find(s => s.is_default) || sigs[0];
            if (def) setSelectedSignatureId(def.signature_id);
        });
    }, [open, getAuthHeader]);

    // Auto-fill default email body when contact + playbook change
    useEffect(() => {
        if (!open || tab !== 'schedule') return;
        if (emailBody || emailSubject) return;  // don't overwrite user edits
        const mergeVars = {
            contact_first_name: contact?.first_name || '',
            contact_full_name: contactName,
            company_name: account?.name || '',
        };
        setEmailSubject(applyMerge('Meeting invite: {{contact_first_name}}', mergeVars));
        setEmailBody(applyMerge(
            'Hi {{contact_first_name}},\n\nLooking forward to our meeting. Agenda and details below.\n\nBest,',
            mergeVars
        ));
        setCalendarDescription(
            'Agenda:\n- Introductions\n- Discovery\n- Next steps'
        );
    }, [open, tab, contact, account, contactName, emailBody, emailSubject]);

    // Apply a template
    const applyTemplate = useCallback((templateId) => {
        setSelectedTemplateId(templateId);
        if (templateId === 'none') return;
        const tpl = templates.find(t => t.template_id === templateId || t.id === templateId);
        if (!tpl) return;
        const vars = {
            contact_first_name: contact?.first_name || '',
            contact_full_name: contactName,
            company_name: account?.name || '',
        };
        if (tpl.subject) setEmailSubject(applyMerge(tpl.subject, vars));
        if (tpl.body || tpl.body_html) setEmailBody(applyMerge(tpl.body || tpl.body_html, vars));
    }, [templates, contact, contactName, account]);

    const signatureHtml = useMemo(() => {
        const sig = signatures.find(s => s.signature_id === selectedSignatureId);
        return sig?.html || sig?.signature_html || '';
    }, [signatures, selectedSignatureId]);

    const emailPreviewWithSignature = useMemo(() => {
        if (!signatureHtml) return emailBody;
        return `${emailBody}\n\n— —\n${signatureHtml.replace(/<[^>]+>/g, '').trim()}`;
    }, [emailBody, signatureHtml]);

    // AI rewrite — reuse the existing /api/ai/write-email endpoint
    const aiRewrite = useCallback(async (target) => {
        setAiRewriting(true);
        try {
            const existing = target === 'email' ? emailBody : calendarDescription;
            const res = await axios.post(
                `${API}/ai/write-email`,
                {
                    existing_draft: existing || '',
                    prompt: existing
                        ? 'Rewrite this meeting invitation to be warmer, clearer, and concise.'
                        : `Write a meeting invitation to ${contactName || 'the contact'} about the upcoming meeting titled "${title}".`,
                    contact_id: contact?.contact_id,
                    account_id: account?.account_id,
                    deal_id: deal?.deal_id,
                    agent_type: 'email_writer',
                    scope: 'full',
                },
                { headers: getAuthHeader() }
            );
            const rewritten = res.data?.body || res.data?.content || res.data?.text || '';
            if (!rewritten) throw new Error('AI returned empty response');
            if (target === 'email') {
                if (res.data?.subject) setEmailSubject(res.data.subject);
                setEmailBody(rewritten);
            } else {
                setCalendarDescription(rewritten);
            }
            toast.success('AI rewrite complete');
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || 'AI rewrite failed');
        } finally {
            setAiRewriting(false);
        }
    }, [emailBody, calendarDescription, contactName, title, contact, account, deal, getAuthHeader]);

    // ─────────────────────────────────────────────────────────────
    // Submit — Record
    // ─────────────────────────────────────────────────────────────
    const submitRecord = async () => {
        if (!title.trim()) { toast.error('Title required'); return; }
        setSaving(true);
        try {
            // 1. Create the meeting_session as completed so post-meeting analysis
            //    can be attached later.
            const sessRes = await axios.post(`${API}/meeting-sessions`, {
                title,
                contact_ids: contact ? [contact.contact_id] : [],
                account_id: account?.account_id,
                deal_id: deal?.deal_id,
                playbook_id: playbookId !== 'none' ? playbookId : null,
                source: 'manual-record',
            }, { headers: getAuthHeader() });
            const sessionId = sessRes.data.session_id;
            // Transition to completed so it shows up in the "Completed" filter.
            await axios.patch(`${API}/meeting-sessions/${sessionId}/status`,
                { status: 'completed' }, { headers: getAuthHeader() }).catch(() => null);

            // 2. Log as an Activity on the contact timeline.
            await axios.post(`${API}/activities`, {
                type: 'meeting',
                title,
                notes,
                contact_id: contact?.contact_id,
                account_id: account?.account_id,
                deal_id: deal?.deal_id,
                completed: true,
                completed_at: `${date}T${startTime}:00`,
                session_id: sessionId,
            }, { headers: getAuthHeader() }).catch(() => null);

            toast.success('Meeting recorded');
            onDone?.({ kind: 'recorded', session_id: sessionId });
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to record meeting');
        } finally {
            setSaving(false);
        }
    };

    // ─────────────────────────────────────────────────────────────
    // Submit — Schedule
    // ─────────────────────────────────────────────────────────────
    const submitSchedule = async ({ sendInvite }) => {
        if (!title.trim()) { toast.error('Title required'); return; }
        if (!date) { toast.error('Date required'); return; }
        setSaving(true);
        try {
            // 1. Create meeting_session in prep with playbook, so the Copilot
            //    can pick it up pre-configured.
            const sessRes = await axios.post(`${API}/meeting-sessions`, {
                title,
                contact_ids: contact ? [contact.contact_id] : [],
                account_id: account?.account_id,
                deal_id: deal?.deal_id,
                playbook_id: playbookId !== 'none' ? playbookId : null,
                source: 'scheduled',
            }, { headers: getAuthHeader() });
            const sessionId = sessRes.data.session_id;

            // 2. Create the Google Calendar event (best-effort).
            const startDateTime = `${date}T${startTime}:00`;
            const endDateTime = `${date}T${endTime}:00`;
            const attendees = contact?.email ? [contact.email] : [];
            try {
                await axios.post(`${API}/oauth/calendar/events`, {
                    summary: title,
                    description: calendarDescription,
                    start_time: startDateTime,
                    end_time: endDateTime,
                    attendees,
                    location: location === 'google_meet' ? 'Google Meet'
                            : location === 'zoom' ? 'Zoom' : location,
                    add_meet: location === 'google_meet',
                }, { headers: getAuthHeader() });
            } catch (calErr) {
                console.warn('Calendar create failed', calErr);
            }

            // 3. Send invite email (best-effort) with selected signature.
            let inviteSent = false;
            if (sendInvite && contact?.email) {
                const sigHtml = signatureHtml || '';
                const html = `<p>${emailBody.replace(/\n/g, '<br/>')}</p>${sigHtml ? `<hr/>${sigHtml}` : ''}`;
                if (gmailConnected || outlookConnected) {
                    try {
                        await sendEmailMutation.mutateAsync({
                            to: contact.email,
                            subject: emailSubject,
                            body: html,
                            gmailConnected,
                            outlookConnected,
                            contact_id: contact.contact_id,
                            ...(account?.account_id && { account_id: account.account_id }),
                        });
                        inviteSent = true;
                    } catch (sendErr) {
                        console.warn('Email send failed, falling back to activity log only', sendErr);
                    }
                } else {
                    toast.warning('Meeting saved without sending an invite because no email provider is connected.');
                }
            }

            // 4. Log activity on the contact timeline.
            await axios.post(`${API}/activities`, {
                type: 'meeting',
                title,
                notes: `${calendarDescription}\n\n---\nInvite email:\n${emailBody}`,
                contact_id: contact?.contact_id,
                account_id: account?.account_id,
                deal_id: deal?.deal_id,
                scheduled_at: startDateTime,
                session_id: sessionId,
            }, { headers: getAuthHeader() }).catch(() => null);

            const successMessage = sendInvite
                ? (inviteSent ? 'Meeting scheduled and invite sent' : 'Meeting scheduled (invite not sent)')
                : 'Meeting scheduled';
            toast.success(successMessage);
            onDone?.({ kind: 'scheduled', session_id: sessionId });
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to schedule meeting');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-4xl max-h-[90vh] overflow-y-auto"
                data-testid="meeting-action-modal"
            >
                <DialogHeader>
                    <DialogTitle>
                        {tab === 'record' ? 'Record a past meeting' : 'Schedule a meeting'}
                    </DialogTitle>
                </DialogHeader>

                {/* Tabs */}
                <div className="flex items-center bg-stone-100 rounded-lg p-0.5 w-fit mb-4">
                    <button
                        onClick={() => setTab('record')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            tab === 'record' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                        }`}
                        data-testid="tab-record"
                    >
                        <Mic className="w-3.5 h-3.5" /> Record meeting
                    </button>
                    <button
                        onClick={() => setTab('schedule')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                            tab === 'schedule' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                        }`}
                        data-testid="tab-schedule"
                    >
                        <CalendarIcon className="w-3.5 h-3.5" /> Schedule meeting
                    </button>
                </div>

                {/* Shared fields */}
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">Title</Label>
                        <Input value={title} onChange={e => setTitle(e.target.value)}
                            placeholder="Discovery call, QBR, demo…" />
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label className="text-xs">Date</Label>
                            <Input type="date" value={date} onChange={e => setDate(e.target.value)} />
                        </div>
                        <div>
                            <Label className="text-xs">Start time</Label>
                            <Input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
                        </div>
                        <div>
                            <Label className="text-xs">End time</Label>
                            <Input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs flex items-center gap-1">
                            <BookOpen className="w-3 h-3" /> Playbook
                            <span className="text-stone-400 text-[10px] font-normal">(pre-selected in Copilot)</span>
                        </Label>
                        <Select value={playbookId} onValueChange={setPlaybookId}>
                            <SelectTrigger data-testid="playbook-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {playbooks.map(p => (
                                    <SelectItem key={p.playbook_id} value={p.playbook_id}>{p.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {tab === 'record' ? (
                        <div>
                            <Label className="text-xs">Notes</Label>
                            <Textarea value={notes} onChange={e => setNotes(e.target.value)}
                                rows={6} placeholder="What was discussed, outcomes, next steps…" />
                        </div>
                    ) : (
                        <>
                            <div>
                                <Label className="text-xs">Location</Label>
                                <Select value={location} onValueChange={setLocation}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="google_meet">Google Meet (auto-link)</SelectItem>
                                        <SelectItem value="zoom">Zoom</SelectItem>
                                        <SelectItem value="phone">Phone</SelectItem>
                                        <SelectItem value="in_person">In person</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs flex items-center gap-1">
                                        <FileText className="w-3 h-3" /> Email template
                                    </Label>
                                    <Select value={selectedTemplateId} onValueChange={applyTemplate}>
                                        <SelectTrigger data-testid="template-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">None (custom)</SelectItem>
                                            {templates.map(t => (
                                                <SelectItem key={t.template_id || t.id} value={t.template_id || t.id}>
                                                    {t.name || t.title}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs flex items-center gap-1">
                                        <PenLine className="w-3 h-3" /> Signature
                                    </Label>
                                    <Select value={selectedSignatureId} onValueChange={setSelectedSignatureId}>
                                        <SelectTrigger data-testid="signature-select"><SelectValue placeholder="Default" /></SelectTrigger>
                                        <SelectContent>
                                            {signatures.length === 0 && <SelectItem value="default">(no signatures)</SelectItem>}
                                            {signatures.map(s => (
                                                <SelectItem key={s.signature_id} value={s.signature_id}>
                                                    {s.name}{s.is_default ? ' (default)' : ''}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Side-by-side email + calendar preview */}
                            <div className="grid grid-cols-2 gap-3 pt-2">
                                <div className="space-y-2 border border-stone-200 rounded-lg p-3 bg-stone-50/40">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-xs flex items-center gap-1 font-semibold text-stone-700">
                                            <Mail className="w-3.5 h-3.5" /> Email invite
                                        </Label>
                                        <button
                                            type="button"
                                            onClick={() => aiRewrite('email')}
                                            disabled={aiRewriting}
                                            className="flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-violet-50 text-violet-700 hover:bg-violet-100 disabled:opacity-50"
                                            data-testid="ai-rewrite-email"
                                        >
                                            {aiRewriting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                            AI rewrite
                                        </button>
                                    </div>
                                    <Input
                                        value={emailSubject}
                                        onChange={e => setEmailSubject(e.target.value)}
                                        placeholder="Subject"
                                        className="text-sm"
                                    />
                                    <Textarea
                                        value={emailBody}
                                        onChange={e => setEmailBody(e.target.value)}
                                        rows={10}
                                        placeholder="Email body…"
                                        className="text-sm"
                                    />
                                    <div className="text-[11px]">
                                        {gmailConnected ? (
                                            <span className="inline-flex items-center gap-1 text-green-600">
                                                <span className="w-2 h-2 rounded-full bg-green-500" />
                                                Invite will send via {gmailStatus?.email || 'Gmail'}
                                            </span>
                                        ) : outlookConnected ? (
                                            <span className="inline-flex items-center gap-1 text-blue-600">
                                                <span className="w-2 h-2 rounded-full bg-blue-500" />
                                                Invite will send via {outlookStatus?.email || 'Outlook'}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-amber-600">
                                                <span className="w-2 h-2 rounded-full bg-amber-500" />
                                                No email provider connected — “Schedule & send invite” will save the meeting only
                                            </span>
                                        )}
                                    </div>
                                    {signatureHtml && (
                                        <div className="text-[10px] text-stone-400 italic">
                                            Signature: {signatures.find(s => s.signature_id === selectedSignatureId)?.name} will be appended
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-2 border border-stone-200 rounded-lg p-3 bg-stone-50/40">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-xs flex items-center gap-1 font-semibold text-stone-700">
                                            <CalendarIcon className="w-3.5 h-3.5" /> Calendar invite
                                        </Label>
                                        <button
                                            type="button"
                                            onClick={() => aiRewrite('calendar')}
                                            disabled={aiRewriting}
                                            className="flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-violet-50 text-violet-700 hover:bg-violet-100 disabled:opacity-50"
                                            data-testid="ai-rewrite-calendar"
                                        >
                                            {aiRewriting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                            AI rewrite
                                        </button>
                                    </div>
                                    <Input
                                        value={title}
                                        onChange={e => setTitle(e.target.value)}
                                        placeholder="Event title"
                                        className="text-sm"
                                    />
                                    <Textarea
                                        value={calendarDescription}
                                        onChange={e => setCalendarDescription(e.target.value)}
                                        rows={10}
                                        placeholder="Agenda + details included in the calendar event description…"
                                        className="text-sm"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-stone-200">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        Cancel
                    </Button>
                    {tab === 'record' ? (
                        <Button onClick={submitRecord} disabled={saving} data-testid="submit-record">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Mic className="w-4 h-4 mr-1.5" />}
                            Record meeting
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="outline"
                                onClick={() => submitSchedule({ sendInvite: false })}
                                disabled={saving}
                                data-testid="submit-schedule-save"
                            >
                                Save without sending
                            </Button>
                            <Button
                                onClick={() => submitSchedule({ sendInvite: true })}
                                disabled={saving}
                                data-testid="submit-schedule-send"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                                Schedule &amp; send invite
                            </Button>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
